/**
 * src/components/ui/Card.jsx
 */
import { Pista } from './Pista'

export function Card({ children, title, subtitle, action, style: extra = {},
                       padding = 'var(--space-5)' }) {
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
      ...extra,
    }}>
      {(title || action) && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div>
            <div style={{
              fontSize: 'var(--text-base)', fontWeight: 'var(--weight-semibold)',
              color: 'var(--color-text)', letterSpacing: '-0.01em',
            }}>{title}</div>
            {subtitle && (
              <div style={{
                fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginTop: '1px',
              }}>
                {subtitle}
              </div>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      <div style={{ padding }}>{children}</div>
    </div>
  )
}

/**
 * Tarjeta de cifra grande.
 *
 * ⚠️ Antes recibía `value` YA COCINADO, y por eso todas las llamadas se escribían
 * igual: `value={isLoading ? '—' : (x ?? 0)}`. Eso parece defensivo y no lo es —
 * cubre el rato de carga y **no cubre la respuesta que llega incompleta o que
 * falla**. En cuanto `isLoading` pasa a false con un dato ausente, el `?? 0` se
 * dispara y la tarjeta pinta un CERO con toda su seguridad. Es el §3 de
 * docs/ERRORES_RECURRENTES.md, el error más repetido del proyecto, escondido
 * dentro de un ternario que parecía cuidadoso.
 *
 * Ahora la tarjeta recibe el valor CRUDO y decide ella, que es la única forma de
 * que quien llama no pueda colapsar los tres casos en uno:
 *
 *   cargando           → «…»   todavía no se sabe
 *   valor null/undef   → «—»   no se ha podido medir, y se dice
 *   valor === 0        → «0»   medido, y es cero (que es información)
 *
 * `value` sigue existiendo solo para reventar de forma visible: una migración a
 * medias que dejara la mitad de las llamadas con el prop viejo sería otro fallo
 * mudo, y ya me ha pasado en este mismo rediseño.
 *
 * Esto NO sustituye a `<Cifra>`. Cifra es para lecturas del MERCADO, que pueden
 * estar rancias y por eso exigen fecha. Esto es para recuentos vivos de la base,
 * donde la fecha sería ceremonia sin información — lo dice la propia Cifra.
 *
 * @param {string} label       rótulo de la tarjeta
 * @param {number|null} valor  la cifra cruda, sin coalescer
 * @param {boolean} [cargando] si la petición sigue en vuelo
 * @param {string} [nada]      qué es lo que no hay, para el caso cero
 * @param {string} [sub]       línea de contexto
 * @param {string} [sufijo]    unidad tras la cifra (ej. «%»); no se pinta si no hay cifra
 * @param {string} [color]     color de la cifra cuando está medida
 */
// `pista` (05-09): la salvaguarda de un KPI, que sale al pedirla. Nace del
// titular del Embudo —«CONVERSIÓN GLOBAL»—, que cuenta como contratación tanto
// una suscripción activa como una prueba gratuita: quien lo lee sin saberlo se
// lleva una tasa inflada. La cifra no cambia; lo que cambia es que ahora se
// puede preguntar qué hay dentro sin escribirlo debajo para siempre.
export function KpiCard({ label, valor, cargando = false, sub, nada, sufijo, color = 'var(--color-accent)', pista, value }) {
  const estado = value !== undefined ? 'prop-viejo'
    : cargando ? 'cargando'
    : (valor === null || valor === undefined) ? 'sin-medir'
    : valor === 0 ? 'cero'
    : 'medido'

  const texto = {
    'cargando': '…',
    'sin-medir': '—',
    'cero': '0',
    'medido': typeof valor === 'number' ? valor.toLocaleString('es-CL') : String(valor),
  }[estado]

  const colorCifra = {
    'cargando': 'var(--color-text-3)',
    'sin-medir': 'var(--color-text-3)',
    'cero': color,
    'medido': color,
  }[estado]

  // Cuando no se ha podido medir, la nota lo dice y pisa al `sub`: un contexto
  // que describe una cifra inexistente confunde más que ayudar.
  const nota = estado === 'sin-medir' ? 'sin medir' : (estado === 'cero' ? (nada || sub) : sub)
  const colorNota = estado === 'sin-medir' ? 'var(--color-warn)' : 'var(--color-text-3)'

  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      padding: 'var(--space-4) var(--space-5)',
    }}>
      <div style={{
        fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-medium)',
        color: 'var(--color-text-3)', letterSpacing: '0.1em',
        marginBottom: 'var(--space-2)', fontFamily: 'var(--font-mono)',
        display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
      }}>
        {label}
        {pista && <Pista contenido={pista} etiqueta={label} />}
      </div>

      {estado === 'prop-viejo' ? (
        // Ruidoso a propósito, igual que `<Cifra>` sin lectura.
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
          color: 'var(--color-error)', background: 'var(--color-error-soft)',
          border: '1px solid var(--color-error-line)',
          borderRadius: 'var(--radius-sm)', padding: 'var(--space-1) var(--space-2)',
        }}>
          ⚠ KpiCard con `value`: usa `valor` + `cargando`
        </div>
      ) : (
        <div style={{
          fontSize: 'var(--text-2xl)', fontWeight: 'var(--weight-bold)', color: colorCifra,
          fontFamily: 'var(--font-mono)', lineHeight: 1, letterSpacing: '-0.02em',
        }}>
          {texto}{sufijo && (estado === 'cero' || estado === 'medido') ? sufijo : ''}
        </div>
      )}

      {nota && (
        <div style={{
          fontSize: 'var(--text-xs)', color: colorNota,
          marginTop: 'var(--space-1)',
        }}>
          {nota}
        </div>
      )}
    </div>
  )
}
